import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress, Typography } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { ThemeProvider } from './theme/ThemeProvider';
import PrivateRoute from './components/PrivateRoute';
import { ErrorBoundary } from './components';
import theme from './theme';

// Auth pages
import Login from './pages/auth/Login';
import SignUp from './pages/auth/SignUp';
import SchoolProfile from './pages/auth/SchoolProfile';

// Dashboard and error pages
import Dashboard from './pages/Dashboard';
import TaggingIdentifier from './pages/TaggingIdentifier';
import NotificationsPage from './pages/NotificationsPage';
import NotFound from './pages/NotFound';

// Module imports
import TeachersModule from './pages/teachers';
import PickupLogsPage from './pages/PickupLogsPage';
import SettingsModule from './pages/settings';
import HelpSupport from './pages/HelpSupport';

// Lazy load other modules
const StudentsModule = React.lazy(() => import('./pages/students'));
const ParentsModule = React.lazy(() => import('./pages/parents'));
const GuestsModule = React.lazy(() => import('./pages/guests'));
const CarsModule = React.lazy(() => import('./pages/cars'));
const StatsModule = React.lazy(() => import('./pages/stats'));

// Full-screen loading component for token authentication
function TokenLoadingScreen() {
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#ffffff',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3
      }}
    >
      <CircularProgress 
        size={60} 
        thickness={4} 
        sx={{ 
          color: '#ff6b35', // SafeLine flame orange
          animation: 'spin 1s linear infinite'
        }} 
      />
      <Typography 
        variant="h6" 
        sx={{ 
          color: '#374151', // Medium charcoal from theme
          fontWeight: 'medium',
          textAlign: 'center'
        }}
      >
        Authenticating...
      </Typography>
      <Typography 
        variant="body2" 
        sx={{ 
          color: '#6b7280', // Light charcoal from theme
          textAlign: 'center',
          maxWidth: '300px'
        }}
      >
        Please wait while we verify your credentials and redirect you to the dashboard.
      </Typography>
    </Box>
  );
}

// Component to handle token authentication
function TokenHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithToken, currentUser, loading, tokenLoading } = useAuth();

  useEffect(() => {
    const handleTokenLogin = async () => {
      // Skip if we're already loading or user is authenticated
      if (loading || currentUser) return;
      
      const urlParams = new URLSearchParams(location.search);
      const token = urlParams.get('token');
      
      if (token) {
        console.log('🎫 Token found in URL, attempting auto-login...');
        
        try {
          // Clear the token from URL immediately for security
          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);
          
          // Attempt to login with the token
          await loginWithToken(token);
          
          console.log('✅ Auto-login successful, redirecting to dashboard...');
          navigate('/dashboard', { replace: true });
          
        } catch (error) {
          console.error('❌ Token login failed:', error);
          // Redirect to login page if token is invalid
          navigate('/login', { replace: true });
        }
      }
    };

    handleTokenLogin();
  }, [location, loginWithToken, navigate, currentUser, loading]);

  // Show loading screen during token authentication
  if (tokenLoading) {
    return <TokenLoadingScreen />;
  }

  return null; // This component doesn't render anything when not loading
}

// Main App Routes component
function AppRoutes() {
  return (
    <React.Suspense fallback={<div>Loading...</div>}>
      <TokenHandler />
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/create-school" element={<SchoolProfile />} />
        
        {/* Protected Routes */}
        <Route element={<PrivateRoute />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/pickup-logs" element={<PickupLogsPage />} />
          <Route path="/tagging-identifier" element={<TaggingIdentifier />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          
          {/* Module Routes */}
          <Route path="/teachers/*" element={<TeachersModule />} />
          <Route path="/students/*" element={<StudentsModule />} />
          <Route path="/parents/*" element={<ParentsModule />} />
          <Route path="/guests/*" element={<GuestsModule />} />
          <Route path="/cars/*" element={<CarsModule />} />
          <Route path="/stats/*" element={<StatsModule />} />
          <Route path="/settings/*" element={<SettingsModule />} />
          <Route path="/help" element={<HelpSupport />} />
        </Route>
        
        {/* Redirect root to dashboard or login based on auth state */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        
        {/* Catch all - show 404 page */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </React.Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        <ThemeProvider>
          <AuthProvider>
            <NotificationsProvider>
              <Router>
                <AppRoutes />
              </Router>
            </NotificationsProvider>
          </AuthProvider>
        </ThemeProvider>
      </MuiThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
